import { useCallback, useRef } from 'react';
import {
  writeBatch,
  doc,
  arrayUnion,
  collection
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './useAuth';
import {
  calculateNextOccurrenceDate,
  ensureValidDate,
  buildTransactionDraftFromRecurring,
  isSupportedFrequency
} from '../features/recurring/recurringLogic';
import { generateId } from '../utils/id';
import { getCurrentRomeTime } from '../utils/formatters';
import { DEBT_WALLET_ID, CREDIT_WALLET_ID } from '../constants/debtWallets';

const DEFAULT_MAX_ITERATIONS = 24;

export const useRecurringTransactions = () => {
  const { user } = useAuth();
  const processedRef = useRef(new Map());
  const isProcessingRef = useRef(false);

  const processRecurringTransactions = useCallback(async (recurringList = [], options = {}) => {
    if (isProcessingRef.current) {
      return {
        totalGenerated: 0,
        summary: []
      };
    }

    if (!user) {
      console.error('Utente non autenticato durante processRecurringTransactions');
      return { totalGenerated: 0, summary: [] };
    }

    isProcessingRef.current = true;

    try {
      console.log(`[DEBUG] processRecurringTransactions start. Count: ${recurringList.length}`);
      const now = getCurrentRomeTime();
      const maxIterations = Number.isInteger(options.maxIterations) && options.maxIterations > 0
        ? options.maxIterations
        : DEFAULT_MAX_ITERATIONS;
      const onStoresUpdate = options.onStoresUpdate;

      const batch = writeBatch(db);
      let operationsCount = 0;
      let totalGenerated = 0;
      const summary = [];
      const newStoreNames = new Set();

      for (const entry of recurringList) {
        if (!entry || entry.active === false) {
          continue;
        }

        const entryType = entry.type === 'income' ? 'income' : 'expense';
        const entryId = entry.id || entry.documentId;
        const currentNextOccurrence = entry.nextOccurrence;

        if (!entryId) {
          continue;
        }

        const previousProcessing = processedRef.current.get(entryId);

        if (previousProcessing && previousProcessing.nextOccurrence !== currentNextOccurrence) {
          processedRef.current.delete(entryId);
        }

        if (!currentNextOccurrence) {
          processedRef.current.delete(entryId);
          continue;
        }

        if (previousProcessing && previousProcessing.nextOccurrence === currentNextOccurrence) {
          continue;
        }

        if (!isSupportedFrequency(entry.frequency)) {
          console.warn('Skipping recurring transaction with unsupported frequency', entry);
          continue;
        }

        let pointerDate;

        try {
          pointerDate = ensureValidDate(currentNextOccurrence);
        } catch (error) {
          console.error('Skipping recurring transaction with invalid nextOccurrence', entry, error);
          continue;
        }

        let iterations = 0;
        let lastGeneratedIso = null;
        let generatedThisRun = 0;
        const nowIso = new Date().toISOString();

        while (pointerDate && pointerDate <= now && iterations < maxIterations) {
          let draft;

          try {
            draft = buildTransactionDraftFromRecurring(entry);
          } catch (error) {
            console.error('Skipping recurring transaction due to invalid configuration', entry, error);
            break;
          }

          const occurrenceIso = pointerDate.toISOString();
          const transactionId = generateId(entryType === 'income' ? 'income' : 'expense');

          const isDebtTransaction = Boolean(draft.isDebtTransaction);
          const resolvedDebtType = isDebtTransaction
            ? (draft.debtType === 'credit' ? 'credit' : 'debt')
            : null;
          const resolvedWalletId = isDebtTransaction
            ? (resolvedDebtType === 'credit' ? CREDIT_WALLET_ID : DEBT_WALLET_ID)
            : draft.walletId;

          const payload = {
            ...draft,
            walletId: resolvedWalletId,
            id: transactionId,
            date: occurrenceIso,
            isFutureTransaction: false,
            isDebtTransaction,
            linkedDebtId: null,
            debtPending: isDebtTransaction,
            createdFromRecurring: true,
            recurringTransactionId: entry.id || entry.documentId || transactionId,
            recurringFrequency: entry.frequency,
            createdAt: nowIso,
            updatedAt: nowIso
          };

          if (!isDebtTransaction) {
            delete payload.debtPersonName;
            delete payload.debtType;
            payload.debtPending = false;
          } else {
            payload.debtPersonName = draft.debtPersonName;
            payload.debtType = resolvedDebtType;
          }

          // Aggiungi al batch
          const collectionName = entryType === 'income' ? 'incomes' : 'expenses';
          const docRef = doc(db, 'users', user.uid, collectionName, transactionId);
          batch.set(docRef, payload);
          operationsCount++;

          // Raccogli nomi store per aggiornamento unico
          if ((collectionName === 'expenses' || collectionName === 'incomes') && draft.store && draft.store.trim()) {
            newStoreNames.add(draft.store.trim());
          }

          console.log('[DEBUG] Staging recurring transaction for batch', transactionId);

          totalGenerated += 1;
          iterations += 1;
          lastGeneratedIso = occurrenceIso;
          generatedThisRun += 1;

          pointerDate = calculateNextOccurrenceDate(pointerDate, entry.frequency);
        }

        if (iterations > 0) {
          const updatePayload = {
            lastOccurrence: lastGeneratedIso,
            nextOccurrence: pointerDate ? pointerDate.toISOString() : null,
            occurrencesGenerated: Number(entry.occurrencesGenerated || 0) + iterations,
            updatedAt: new Date().toISOString()
          };

          const recurringRef = doc(db, 'users', user.uid, 'recurringTransactions', entry.id || entry.documentId);
          batch.update(recurringRef, updatePayload);
          operationsCount++;

          processedRef.current.set(entryId, {
            nextOccurrence: currentNextOccurrence,
            processedAt: Date.now()
          });

          summary.push({
            id: entry.id || entry.documentId,
            generated: iterations
          });
        } else if (pointerDate && pointerDate > now) {
          processedRef.current.delete(entryId);
        }
      }

      // Aggiorna gli store se necessario (una sola operazione)
      if (newStoreNames.size > 0) {
        const storesRef = doc(db, 'users', user.uid, 'stores', 'default');
        batch.set(storesRef, {
          stores: arrayUnion(...Array.from(newStoreNames))
        }, { merge: true });
        operationsCount++;
      }

      if (operationsCount > 0) {
        console.log(`[DEBUG] Committing batch with ${operationsCount} operations`);
        await batch.commit();

        // Notifica aggiornamento store locale se richiesto
        if (newStoreNames.size > 0 && typeof onStoresUpdate === 'function') {
          // Nota: non abbiamo la lista completa degli store qui, quindi potremmo doverla ricaricare o gestire diversamente.
          // Per ora passiamo solo i nuovi, chi riceve dovrà gestire il merge o invalidare.
          // Ma visto che useFirestore.js faceva arrayUnion e basta, l'update locale era ottimistico o ricaricava.
          // Qui non ricarichiamo.
        }
      } else {
        console.log('[DEBUG] No operations to commit');
      }

      return {
        totalGenerated,
        summary
      };
    } catch (error) {
      console.error('Error processing recurring transactions batch:', error);
      throw error;
    } finally {
      isProcessingRef.current = false;
    }
  }, [user]);

  return {
    processRecurringTransactions
  };
};
